import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, MapPin, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/Input';
import { KeywordChips } from '@/components/ui/KeywordChips';
import type { LicitacaoFonte, LicitacoesFilters } from '@/hooks/useLicitacoes';
import type { LicitacaoFaceta, LicitacoesMeta } from '@/hooks/useLicitacoesMeta';
import {
  CATEGORIAS, ESFERAS, MODALIDADES, REGIOES_LABELS, REGIOES_UFS_FALLBACK, UFS_BR,
} from '@/lib/licitacoesOpcoes';
import { somenteDigitos } from './filtrosAtivos';

interface EmpresaResumo {
  id: number;
  razao_social: string;
}

interface FiltrosAvancadosLicitacoesProps {
  filters: LicitacoesFilters;
  /** Aplica um patch parcial sobre os filtros (o chamador reseta a paginação). */
  onPatch: (patch: Partial<LicitacoesFilters>) => void;
  meta?: LicitacoesMeta;
  /** `/licitacoes/meta` falhou: a tela cai para listas estáticas e campos livres. */
  metaIndisponivel?: boolean;
  fontes?: LicitacaoFonte[];
  empresas?: EmpresaResumo[];
}

const selectClassName =
  'bg-ds-surface2 border border-ds-border rounded-lg px-3 py-2 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50';

const chipClassName = (ativo: boolean) =>
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand focus-visible:ring-offset-1 focus-visible:ring-offset-ds-bg ' +
  (ativo
    ? 'border-ds-success bg-ds-success/15 text-ds-success font-medium'
    : 'border-ds-border bg-ds-surface2 text-ds-text-primary hover:border-ds-success/50');

function ChipToggle({
  ativo, onClick, ariaLabel, children,
}: {
  ativo: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <button type="button" aria-pressed={ativo} aria-label={ariaLabel} onClick={onClick} className={chipClassName(ativo)}>
      {children}
    </button>
  );
}

function Campo({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ds-text-secondary">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-ds-text-secondary">{hint}</span>}
    </div>
  );
}

function Secao({ titulo, children, icone }: { titulo: string; children: ReactNode; icone?: ReactNode }) {
  return (
    <section role="group" aria-label={titulo} className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ds-text-secondary">
        {icone}
        {titulo}
      </h3>
      {children}
    </section>
  );
}

const alternarValor = (lista: string[] | undefined, valor: string) => {
  const atual = lista || [];
  const proximo = atual.includes(valor) ? atual.filter((item) => item !== valor) : [...atual, valor];
  return proximo.length > 0 ? proximo : undefined;
};

const contagem = (facetas: LicitacaoFaceta[] | undefined, valor: string) =>
  facetas?.find((faceta) => faceta.valor === valor)?.total;

function FiltrosAvancadosLicitacoes({
  filters, onPatch, meta, metaIndisponivel, fontes, empresas,
}: FiltrosAvancadosLicitacoesProps) {
  const [cnpjDraft, setCnpjDraft] = useState(filters.orgao_cnpj || '');
  const cnpjDigitos = somenteDigitos(cnpjDraft);

  // Mantém o campo de CNPJ em sincronia quando o filtro muda de fora
  // (remoção de um chip, "limpar tudo" ou aplicação de uma busca salva),
  // sem desfazer a pontuação que o usuário digitou.
  useEffect(() => {
    if (!filters.orgao_cnpj && cnpjDigitos.length === 14) setCnpjDraft('');
    else if (filters.orgao_cnpj && filters.orgao_cnpj !== cnpjDigitos) setCnpjDraft(filters.orgao_cnpj);
  }, [filters.orgao_cnpj, cnpjDigitos]);

  const regioesMap = meta?.regioes && Object.keys(meta.regioes).length > 0
    ? meta.regioes
    : REGIOES_UFS_FALLBACK;

  const ufsDisponiveis = meta?.ufs && meta.ufs.length > 0
    ? meta.ufs.map((faceta) => faceta.valor)
    : UFS_BR;

  const ufsSelecionadas = filters.ufs_multi || [];

  const alternarUf = (uf: string) => onPatch({ ufs_multi: alternarValor(ufsSelecionadas, uf) });

  const regiaoSelecionada = (regiao: string) => {
    const ufsRegiao = regioesMap[regiao] || [];
    return ufsRegiao.length > 0 && ufsRegiao.every((uf) => ufsSelecionadas.includes(uf));
  };

  // Região é um atalho sobre o mesmo modelo (`ufs_multi`): marcar uma região
  // adiciona as UFs correspondentes, nunca cria um segundo conjunto geográfico.
  const alternarRegiao = (regiao: string) => {
    const ufsRegiao = regioesMap[regiao] || [];
    const proximo = regiaoSelecionada(regiao)
      ? ufsSelecionadas.filter((uf) => !ufsRegiao.includes(uf))
      : [...new Set([...ufsSelecionadas, ...ufsRegiao])];
    onPatch({ ufs_multi: proximo.length > 0 ? proximo : undefined });
  };

  const modalidadesFacetadas = meta?.modalidades && meta.modalidades.length > 0 ? meta.modalidades : null;
  const modalidadesEstaticas = metaIndisponivel ? null : MODALIDADES;
  const categoriasDisponiveis = meta?.categorias && meta.categorias.length > 0
    ? meta.categorias.map((faceta) => faceta.valor)
    : CATEGORIAS;

  const alternarPortal = (portalLabel: string) =>
    onPatch({ portal: alternarValor(filters.portal, portalLabel) });

  const alternarEmpresa = (empresaId: number) => {
    const atual = filters.empresa_ids || [];
    const proximo = atual.includes(empresaId)
      ? atual.filter((id) => id !== empresaId)
      : [...atual, empresaId];
    onPatch({ empresa_ids: proximo.length > 0 ? proximo : undefined });
  };

  return (
    <div className="border-t border-ds-border pt-4 space-y-6">
      {metaIndisponivel && (
        <p className="flex items-start gap-2 rounded-lg border border-ds-warning/30 bg-ds-warning/10 px-3 py-2 text-xs text-ds-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Não foi possível carregar as opções com contagem de resultados. Os filtros continuam
          funcionando com as listas padrão e os campos de texto livre.
        </p>
      )}

      {/* ----------------------------- Geografia ----------------------------- */}
      <Secao titulo="Abrangência geográfica" icone={<MapPin className="h-3.5 w-3.5" aria-hidden="true" />}>
        <p className="text-[11px] text-ds-text-secondary">
          Marque regiões e/ou estados — a região apenas marca os estados dela.
        </p>

        <div role="group" aria-label="Regiões" className="flex flex-wrap gap-2">
          {REGIOES_LABELS.filter((regiao) => (regioesMap[regiao.key] || []).length > 0).map((regiao) => (
            <ChipToggle
              key={regiao.key}
              ativo={regiaoSelecionada(regiao.key)}
              onClick={() => alternarRegiao(regiao.key)}
              ariaLabel={`Região ${regiao.label}`}
            >
              {regiao.label}
            </ChipToggle>
          ))}
        </div>

        <div role="group" aria-label="Estados" className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
          {ufsDisponiveis.map((uf) => {
            const total = contagem(meta?.ufs, uf);
            return (
              <ChipToggle
                key={uf}
                ativo={ufsSelecionadas.includes(uf)}
                onClick={() => alternarUf(uf)}
                ariaLabel={total !== undefined
                  ? `Estado ${uf}, ${total.toLocaleString('pt-BR')} licitações`
                  : `Estado ${uf}`}
              >
                <span>{uf}</span>
                {total !== undefined && (
                  <span className="text-ds-text-secondary">{total.toLocaleString('pt-BR')}</span>
                )}
              </ChipToggle>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Campo label="Cidade">
            <Input
              aria-label="Cidade"
              placeholder="Nome do município"
              value={filters.municipio || ''}
              onChange={(e) => onPatch({ municipio: e.target.value || undefined })}
            />
          </Campo>
        </div>
      </Secao>

      {/* ---------------------- Modalidade e esfera ---------------------- */}
      <Secao titulo="Modalidade e esfera">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Campo label="Modalidades">
            {modalidadesFacetadas || modalidadesEstaticas ? (
              <div role="group" aria-label="Modalidades" className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {(modalidadesFacetadas?.map((faceta) => faceta.valor) || modalidadesEstaticas || []).map((valor) => {
                  const total = contagem(modalidadesFacetadas || undefined, valor);
                  return (
                    <ChipToggle
                      key={valor}
                      ativo={(filters.modalidades || []).includes(valor)}
                      onClick={() => onPatch({
                        modalidades: alternarValor(filters.modalidades, valor),
                        modalidade: undefined,
                      })}
                      ariaLabel={total !== undefined
                        ? `Modalidade ${valor}, ${total.toLocaleString('pt-BR')} licitações`
                        : `Modalidade ${valor}`}
                    >
                      <span>{valor}</span>
                      {total !== undefined && (
                        <span className="text-ds-text-secondary">{total.toLocaleString('pt-BR')}</span>
                      )}
                    </ChipToggle>
                  );
                })}
              </div>
            ) : (
              <Input
                aria-label="Modalidade contém"
                placeholder="Ex.: Pregão"
                value={filters.modalidade || ''}
                onChange={(e) => onPatch({ modalidade: e.target.value || undefined, modalidades: undefined })}
              />
            )}
          </Campo>

          <Campo
            label="Esfera administrativa"
            hint="Preenchido em parte do acervo — filtrar por esfera reduz muito o resultado."
          >
            <div role="group" aria-label="Esfera administrativa" className="flex flex-wrap gap-1.5">
              {ESFERAS.map((esfera) => (
                <ChipToggle
                  key={esfera.valor}
                  ativo={(filters.esferas || []).includes(esfera.valor)}
                  onClick={() => onPatch({ esferas: alternarValor(filters.esferas, esfera.valor) })}
                  ariaLabel={`Esfera ${esfera.label}`}
                >
                  {esfera.label}
                </ChipToggle>
              ))}
            </div>
          </Campo>

          <Campo
            label="CAPAG mínima"
            hint="Capacidade de Pagamento do município (Tesouro). Traz a nota escolhida e as melhores."
          >
            <select
              aria-label="CAPAG mínima"
              value={filters.capag_min || ''}
              onChange={(e) => onPatch({ capag_min: e.target.value || undefined })}
              className={selectClassName}
            >
              <option value="">Qualquer</option>
              <option value="A+">A+</option>
              <option value="A">A ou melhor</option>
              <option value="B+">B+ ou melhor</option>
              <option value="B">B ou melhor</option>
              <option value="C">C ou melhor</option>
              <option value="D">D ou melhor</option>
            </select>
          </Campo>

          <Campo
            label="Risco de deserto"
            hint="Estimativa de concorrência. Risco alto = tende a dar deserta (menos concorrência)."
          >
            <select
              aria-label="Risco de deserto"
              value={filters.risco_deserto?.[0] || ''}
              onChange={(e) => onPatch({ risco_deserto: e.target.value ? [e.target.value] : undefined })}
              className={selectClassName}
            >
              <option value="">Qualquer</option>
              <option value="alto">Alto</option>
              <option value="medio">Médio</option>
              <option value="baixo">Baixo</option>
            </select>
          </Campo>
        </div>
      </Secao>

      {/* ------------------------- Órgão e edital ------------------------- */}
      <Secao titulo="Órgão e edital">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Campo label="Nome do órgão">
            <Input
              aria-label="Nome do órgão"
              placeholder="Ex.: Prefeitura de Curitiba"
              value={filters.orgao_nome || ''}
              onChange={(e) => onPatch({ orgao_nome: e.target.value || undefined })}
            />
          </Campo>

          <Campo
            label="CNPJ do órgão"
            hint={cnpjDigitos.length > 0 && cnpjDigitos.length !== 14
              ? `Informe os 14 dígitos (${cnpjDigitos.length}/14) para aplicar o filtro.`
              : 'Aceita pontuação; o filtro é aplicado com os 14 dígitos.'}
          >
            <Input
              aria-label="CNPJ do órgão"
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
              value={cnpjDraft}
              onChange={(e) => {
                setCnpjDraft(e.target.value);
                const digitos = somenteDigitos(e.target.value);
                onPatch({ orgao_cnpj: digitos.length === 14 ? digitos : undefined });
              }}
            />
          </Campo>

          <Campo label="Número do edital">
            <Input
              aria-label="Número do edital"
              placeholder="Ex.: 001/2026"
              value={filters.numero_edital || ''}
              onChange={(e) => onPatch({ numero_edital: e.target.value || undefined })}
            />
          </Campo>
        </div>
      </Secao>

      {/* --------------------- Valor, prazo e objeto --------------------- */}
      <Secao titulo="Valor, prazo e objeto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Campo label="Valor mínimo (R$)">
            <Input
              aria-label="Valor mínimo"
              type="number"
              min={0}
              placeholder="0"
              value={filters.valor_min ?? ''}
              onChange={(e) => onPatch({ valor_min: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Campo>

          <Campo label="Valor máximo (R$)">
            <Input
              aria-label="Valor máximo"
              type="number"
              min={0}
              placeholder="Sem limite"
              value={filters.valor_max ?? ''}
              onChange={(e) => onPatch({ valor_max: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Campo>

          <Campo label="Ordenar por">
            <div className="flex gap-2">
              <select
                aria-label="Ordenar por"
                value={filters.sort || 'scraped_at'}
                onChange={(e) => onPatch({ sort: e.target.value as LicitacoesFilters['sort'] })}
                className={`flex-1 ${selectClassName}`}
              >
                <option value="scraped_at">Recentes</option>
                <option value="prazo_abertura">Prazo</option>
                <option value="valor_estimado">Valor</option>
                <option value="relevancia_score">Relevância IA</option>
              </select>
              <select
                aria-label="Direção da ordenação"
                value={filters.order || 'desc'}
                onChange={(e) => onPatch({ order: e.target.value as 'asc' | 'desc' })}
                className={selectClassName}
              >
                <option value="desc">↓ Desc</option>
                <option value="asc">↑ Asc</option>
              </select>
            </div>
          </Campo>

          <Campo label="Prazo a partir de">
            <Input
              aria-label="Prazo a partir de"
              type="date"
              value={filters.data_de || ''}
              onChange={(e) => onPatch({ data_de: e.target.value || undefined })}
            />
          </Campo>

          <Campo label="Prazo até">
            <Input
              aria-label="Prazo até"
              type="date"
              value={filters.data_ate || ''}
              onChange={(e) => onPatch({ data_ate: e.target.value || undefined })}
            />
          </Campo>

          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-ds-text-primary">
              <input
                type="checkbox"
                checked={filters.somente_ativas !== false}
                onChange={(e) => onPatch({ somente_ativas: e.target.checked })}
                className="accent-ds-success w-4 h-4"
              />
              Apenas ativas
            </label>
          </div>

          <KeywordChips
            label="Incluir palavras no objeto"
            value={filters.objeto_inclui}
            onChange={(value) => onPatch({ objeto_inclui: value })}
            placeholder="Digite e pressione Enter"
          />

          <KeywordChips
            label="Excluir palavras no objeto"
            value={filters.objeto_exclui}
            onChange={(value) => onPatch({ objeto_exclui: value })}
            placeholder="Digite e pressione Enter"
          />
        </div>
      </Secao>

      {/* ------------------------ Portais e clientes ------------------------ */}
      <Secao titulo="Portais e clientes">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {fontes && fontes.length > 0 && (
            <Campo label="Plataformas">
              <div role="group" aria-label="Plataformas" className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                {fontes.map((fonte) => {
                  const total = contagem(meta?.portais, fonte.portal_label);
                  return (
                    <label
                      key={fonte.id}
                      className="flex items-center gap-1.5 cursor-pointer text-xs text-ds-text-primary bg-ds-surface2 border border-ds-border rounded-full px-2.5 py-1"
                    >
                      <input
                        type="checkbox"
                        aria-label={`Plataforma ${fonte.portal_label}`}
                        checked={(filters.portal || []).includes(fonte.portal_label)}
                        onChange={() => alternarPortal(fonte.portal_label)}
                        className="accent-ds-success w-3.5 h-3.5"
                      />
                      <span>{fonte.portal_label}</span>
                      {total !== undefined && (
                        <span className="text-ds-text-secondary">{total.toLocaleString('pt-BR')}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </Campo>
          )}

          {empresas && empresas.length > 0 && (
            <Campo label="Empresas aderentes">
              <div role="group" aria-label="Empresas aderentes" className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
                {empresas.map((empresa) => (
                  <label
                    key={empresa.id}
                    className="flex items-center gap-1.5 cursor-pointer text-xs text-ds-text-primary bg-ds-surface2 border border-ds-border rounded-full px-2.5 py-1"
                  >
                    <input
                      type="checkbox"
                      aria-label={`Empresa ${empresa.razao_social}`}
                      checked={(filters.empresa_ids || []).includes(empresa.id)}
                      onChange={() => alternarEmpresa(empresa.id)}
                      className="accent-ds-success w-3.5 h-3.5"
                    />
                    <span>{empresa.razao_social}</span>
                  </label>
                ))}
              </div>
            </Campo>
          )}
        </div>
      </Secao>

      {/* --------------------- Diferenciais de IA --------------------- */}
      <section
        role="group"
        aria-label="Inteligência Alpendre"
        className="rounded-xl border border-ds-brand/40 bg-ds-brand/5 p-4 space-y-3"
      >
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-ds-text-primary">
            <Sparkles className="h-4 w-4 text-ds-brand" aria-hidden="true" />
            Inteligência Alpendre
          </h3>
          <p className="text-[11px] text-ds-text-secondary">
            Filtros que só existem aqui: classificação por IA, relevância calculada e cobertura de portais pagos.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Campo label="Categoria (IA)">
            <select
              aria-label="Categoria (IA)"
              value={filters.categoria || ''}
              onChange={(e) => onPatch({ categoria: e.target.value || undefined })}
              className={selectClassName}
            >
              <option value="">Todas as categorias</option>
              {categoriasDisponiveis.map((categoria) => {
                const total = contagem(meta?.categorias, categoria);
                return (
                  <option key={categoria} value={categoria}>
                    {total !== undefined ? `${categoria} (${total.toLocaleString('pt-BR')})` : categoria}
                  </option>
                );
              })}
            </select>
          </Campo>

          <Campo label={`Relevância mínima IA (${filters.relevancia_min ?? 0}%)`}>
            <input
              type="range"
              aria-label="Relevância mínima IA"
              min={0}
              max={100}
              step={10}
              value={filters.relevancia_min ?? 0}
              onChange={(e) => onPatch({ relevancia_min: Number(e.target.value) || undefined })}
              className="w-full accent-ds-success"
            />
          </Campo>

          <Campo label="Acesso ao portal">
            <select
              aria-label="Acesso ao portal"
              value={filters.pago === undefined ? '' : filters.pago ? 'true' : 'false'}
              onChange={(e) => onPatch({ pago: e.target.value === '' ? undefined : e.target.value === 'true' })}
              className={selectClassName}
            >
              <option value="">Pagos e gratuitos</option>
              <option value="false">Apenas gratuitos</option>
              <option value="true">Apenas pagos</option>
            </select>
          </Campo>

          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-ds-text-primary">
              <input
                type="checkbox"
                checked={!!filters.somente_ia}
                onChange={(e) => onPatch({ somente_ia: e.target.checked || undefined })}
                className="accent-ds-success w-4 h-4"
              />
              Apenas processadas por IA
            </label>
          </div>
        </div>
      </section>
    </div>
  );
}

export { FiltrosAvancadosLicitacoes };
